import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getApiAnalysisReport, runApiAnalysisIvCheck } from '../../services/api';
import ReportsIvCompare, { REASON_LABELS } from './ReportsIvCompare';

const PROVIDER_LABELS = { polygon: 'Massive', eodhd: 'EODHD', fred: 'FRED', other: 'Free' };

const BACKUP_LABELS = {
  eodhd: { text: 'EODHD', tone: 'good' },
  polygon: { text: 'Polygon (same provider)', tone: 'warn' },
  none: { text: 'None', tone: 'bad' },
  dead: { text: 'Dead (EODHD 403)', tone: 'bad' },
  'n/a': { text: 'n/a', tone: 'muted' },
};

const ALT_LABELS = {
  yes: { text: 'Yes', tone: 'good' },
  partial: { text: 'Partial', tone: 'warn' },
  no: { text: 'No', tone: 'bad' },
};

const TIMELINESS_LABELS = {
  live: 'Live', near_live: 'Near-live', historical: 'Historical', reference: 'Reference', forward: 'Forward',
};

const SUBSCRIPTION_LABELS = {
  massive_stocks: 'Massive Stocks',
  massive_options: 'Massive Options',
  massive_any: 'Any Massive plan',
  eodhd: 'EODHD All-In-One',
  free: 'Free',
};

function Chip({ tone, children }) {
  return <span className={`api-analysis-chip ${tone}`}>{children}</span>;
}

function ivVerdict(chain, marketOpen) {
  const liquid = chain?.liquid;
  const cov = liquid?.total ? liquid : chain?.coverage;
  if (!cov?.total) return { tone: 'bad', text: 'No contracts returned' };
  const solved = (cov.pct.provider_iv || 0) + (cov.pct.computed_iv || 0);
  const scope = liquid?.total ? 'liquid near-ATM contracts' : 'fetched contracts';
  if (solved >= 90) return { tone: 'good', text: `Healthy: ${solved.toFixed(1)}% of ${scope} have a real IV` };
  if (solved >= 60) {
    return { tone: 'warn', text: `Degraded: ${solved.toFixed(1)}% of ${scope} have a real IV; the rest show a 30% placeholder` };
  }
  return {
    tone: 'bad',
    text: `Poor: only ${solved.toFixed(1)}% of ${scope} have a real IV${marketOpen ? '' : ' (market closed: quotes are frozen)'}`,
  };
}

function IvHealthCheck() {
  const [ticker, setTicker] = useState('AAPL');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const run = async (e) => {
    e.preventDefault();
    if (!ticker.trim()) return;
    setRunning(true);
    setError(null);
    try {
      setResult(await runApiAnalysisIvCheck(ticker.trim().toUpperCase()));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'IV check failed');
    } finally {
      setRunning(false);
    }
  };

  const chain = result?.chain;
  const cov = chain?.coverage;
  const liquid = chain?.liquid;
  const verdict = chain && !chain.error ? ivVerdict(chain, result.market_open) : null;

  return (
    <div className="api-analysis-ivcheck">
      <form className="api-analysis-ivcheck-form" onSubmit={run}>
        <label htmlFor="iv-check-ticker">Live IV health check</label>
        <input
          id="iv-check-ticker"
          value={ticker}
          maxLength={10}
          onChange={(e) => setTicker(e.target.value.toUpperCase())}
          placeholder="Ticker"
        />
        <button type="submit" className="refresh-btn" disabled={running}>
          {running ? 'Checking…' : 'Run IV health check'}
        </button>
        <span className="api-analysis-hint">Fetches 2 chain pages (500 contracts) and solves IV the same way the screener does.</span>
      </form>

      {error && <div className="reports-error"><p>{error}</p></div>}

      {chain && chain.error && (
        <div className="reports-error"><p>Chain fetch failed: {chain.error}</p></div>
      )}

      {chain && !chain.error && cov && (
        <div className="api-analysis-ivcheck-result">
          <div className={`api-analysis-verdict ${verdict.tone}`}>{verdict.text}</div>
          <div className="api-analysis-ivcheck-meta">
            {result.ticker} · underlying {chain.underlying ?? '–'} · {chain.n} contracts
            {chain.expirations ? ` · exp ${chain.expirations[0]} → ${chain.expirations[1]}` : ''}
            {' · '}{result.checked_at_et} · market {result.market_open ? 'open' : 'closed'}
          </div>

          <div className="api-analysis-stat-row">
            <Stat label="App IV used" value={`${cov.pct.computed_iv}%`} sub={`anchored ${cov.pct.anchored_iv}%`} />
            <Stat
              label="Massive fallback"
              value={`${cov.pct.provider_iv}%`}
              sub={cov.pct.provider_iv_present != null ? `Massive sent IV on ${cov.pct.provider_iv_present}%` : 'app could not solve'}
            />
            <Stat label="Unavailable → 30%" value={`${cov.pct.unavailable_iv}%`} sub="placeholder IV" tone="bad" />
            <Stat
              label="Liquid near-ATM"
              value={liquid?.total ? `${(liquid.pct.computed_iv + liquid.pct.provider_iv).toFixed(1)}%` : '–'}
              sub={liquid?.total
                ? `n=${liquid.total} · median IV ${chain.liquid_median_iv != null ? `${(chain.liquid_median_iv * 100).toFixed(1)}%` : '–'}`
                : 'none in fetched pages'}
            />
            <Stat
              label="Data timeframe"
              value={Object.keys(cov.timeframes || {}).join(', ') || '–'}
              sub="DELAYED = 15-min delayed feed"
            />
          </div>

          <div className="api-analysis-two-col">
            <div>
              <h5>Why IV was unavailable</h5>
              {Object.keys(cov.reasons || {}).length ? (
                <ul className="api-analysis-list">
                  {Object.entries(cov.reasons).slice(0, 6).map(([reason, n]) => (
                    <li key={reason}><strong>{n}</strong> {REASON_LABELS[reason] || reason}</li>
                  ))}
                </ul>
              ) : <p className="api-analysis-muted">None</p>}
            </div>
            <div>
              <h5>Option price used</h5>
              <ul className="api-analysis-list">
                {Object.entries(cov.price_sources || {}).sort((a, b) => b[1] - a[1]).map(([src, n]) => (
                  <li key={src}><strong>{n}</strong> {src}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub, tone }) {
  return (
    <div className={`api-analysis-stat ${tone || ''}`}>
      <div className="kpi-label">{label}</div>
      <div className="api-analysis-stat-value">{value}</div>
      {sub && <div className="kpi-sublabel">{sub}</div>}
    </div>
  );
}

function EndpointTable({ endpoints }) {
  return (
    <div className="api-analysis-table-wrap">
      <table className="api-analysis-table">
        <thead>
          <tr>
            <th>Provider</th>
            <th>Call</th>
            <th>Type</th>
            <th>History</th>
            <th>Backup today</th>
            <th>Other provider could serve it?</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          {endpoints.map((e) => {
            const backup = BACKUP_LABELS[e.backup] || BACKUP_LABELS['n/a'];
            const alt = ALT_LABELS[e.alternative.level];
            return (
              <tr key={e.id} className={e.status === 'dead' ? 'dead' : ''}>
                <td>
                  <span className={`api-analysis-provider ${e.provider}`}>{PROVIDER_LABELS[e.provider]}</span>
                  <div className="api-analysis-id">{e.id}</div>
                </td>
                <td>
                  <div className="api-analysis-title">{e.title}</div>
                  <code>{e.path}</code>
                  {e.notes && <div className="api-analysis-note">{e.notes}</div>}
                </td>
                <td>
                  {TIMELINESS_LABELS[e.timeliness]}
                  <div className="api-analysis-muted">{SUBSCRIPTION_LABELS[e.subscription]}</div>
                  {e.status !== 'active' && <Chip tone="muted">{e.status}</Chip>}
                </td>
                <td className="api-analysis-history">{e.history}</td>
                <td>
                  <Chip tone={backup.tone}>{backup.text}</Chip>
                  {e.backup_note && <div className="api-analysis-note">{e.backup_note}</div>}
                </td>
                <td>
                  <Chip tone={alt.tone}>{alt.text}</Chip>
                  <div className="api-analysis-note">{e.alternative.note}</div>
                </td>
                <td className="api-analysis-source">
                  {e.source_files.map((f) => <div key={f}>{f}</div>)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ReportsApiAnalysis() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [provider, setProvider] = useState('all');
  const [noBackupOnly, setNoBackupOnly] = useState(false);
  const [open, setOpen] = useState({ screener_heatmap: true, watchlist_heatmap: true });

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setReport(await getApiAnalysisReport());
    } catch (err) {
      setError(err.message || 'Failed to fetch API analysis');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const endpointsById = useMemo(() => {
    const map = {};
    (report?.endpoints || []).forEach((e) => { map[e.id] = e; });
    return map;
  }, [report]);

  const matches = useCallback((e) => {
    if (provider !== 'all' && e.provider !== provider) return false;
    if (noBackupOnly && !['none', 'dead'].includes(e.backup)) return false;
    return true;
  }, [provider, noBackupOnly]);

  if (loading) {
    return (
      <div className="roles-loading">
        <div className="spinner"></div>
        <p>Loading API analysis...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reports-error">
        <p>{error}</p>
        <button className="refresh-btn" onClick={fetchReport}>Retry</button>
      </div>
    );
  }

  const { summary, features, iv, subscriptions } = report;
  const label = (id) => endpointsById[id]?.title || id;

  return (
    <div className="reports-overview api-analysis">
      <p className="api-analysis-muted">
        Every Massive (Polygon) and EODHD call the app makes, grouped by feature. Catalog as of {report.as_of};
        a backend test fails if a new provider call is added without an entry here.
      </p>

      {/* KPI row */}
      <div className="analytics-kpi-cards api-analysis-kpis">
        <Kpi color="#218aff" label="Paid calls in use" value={summary.endpoints_active}
          sub={`${summary.polygon_active} Massive · ${summary.eodhd_active} EODHD`} />
        <Kpi color="#ef4444" label="Massive, no backup" value={summary.polygon_no_backup.length}
          sub="Outage = feature down (incl. all options)" />
        <Kpi color="#10b981" label="EODHD backup in place" value={summary.with_eodhd_backup.length}
          sub={summary.with_eodhd_backup.map(label).join(', ')} />
        <Kpi color="#8b5cf6" label="EODHD could replace" value={summary.eodhd_could_replace.length}
          sub="Massive calls EODHD already covers" />
        <Kpi color="#f59e0b" label="Need Massive Stocks" value={summary.massive_stocks_dependent.length}
          sub="Break or degrade if Stocks is dropped" />
      </div>

      {/* IV box */}
      <div className="analytics-chart-container api-analysis-iv">
        <div className="analytics-chart-header">
          <h3>Elasticity Heatmap IV</h3>
          <Chip tone="good">App IV · Massive fallback</Chip>
        </div>
        <p className="api-analysis-lead"><strong>{iv.methodology.headline}</strong> {iv.methodology.why}</p>

        <div className="api-analysis-steps">
          {iv.methodology.steps.map((s) => (
            <div key={s.label} className="api-analysis-step">
              <div className="api-analysis-step-label">{s.label}</div>
              <div>{s.detail}</div>
              <code className="api-analysis-ref">{s.ref}</code>
            </div>
          ))}
        </div>

        <IvHealthCheck />

        <ReportsIvCompare />

        <h4>Accuracy risks</h4>
        <ul className="api-analysis-risks">
          {iv.risks.map((r) => (
            <li key={r.title}>
              <Chip tone={r.severity === 'high' ? 'bad' : r.severity === 'medium' ? 'warn' : 'muted'}>{r.severity}</Chip>
              <div>
                <strong>{r.title}</strong>
                <div>{r.detail}</div>
                <code className="api-analysis-ref">{r.ref}</code>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* By feature */}
      <div className="analytics-chart-container">
        <div className="analytics-chart-header">
          <h3>Calls by feature</h3>
          <div className="api-analysis-filters">
            <select className="period-select" value={provider} onChange={(e) => setProvider(e.target.value)}>
              <option value="all">All providers</option>
              <option value="polygon">Massive only</option>
              <option value="eodhd">EODHD only</option>
              <option value="fred">FRED</option>
              <option value="other">Other free</option>
            </select>
            <label className="api-analysis-check">
              <input type="checkbox" checked={noBackupOnly} onChange={(e) => setNoBackupOnly(e.target.checked)} />
              No backup only
            </label>
            <button className="refresh-btn" onClick={() => setOpen(Object.fromEntries(features.map((f) => [f.key, true])))}>
              Expand all
            </button>
            <button className="refresh-btn" onClick={() => setOpen({})}>Collapse all</button>
          </div>
        </div>

        {features.map((f) => {
          const rows = f.endpoint_ids.map((id) => endpointsById[id]).filter(Boolean);
          const shown = rows.filter(matches);
          if (!shown.length) return null;
          const massive = rows.filter((e) => e.provider === 'polygon').length;
          const eodhd = rows.filter((e) => e.provider === 'eodhd').length;
          const isOpen = !!open[f.key];
          return (
            <div key={f.key} className="api-analysis-feature">
              <button
                className="api-analysis-feature-header"
                onClick={() => setOpen((prev) => ({ ...prev, [f.key]: !prev[f.key] }))}
                aria-expanded={isOpen}
              >
                <span className="api-analysis-caret">{isOpen ? '▾' : '▸'}</span>
                <span className="api-analysis-feature-title">{f.title}</span>
                {f.iv_critical && <Chip tone="warn">uses IV</Chip>}
                <span className="api-analysis-feature-counts">
                  {massive} Massive · {eodhd} EODHD
                </span>
              </button>
              {isOpen && (
                <div className="api-analysis-feature-body">
                  <p className="api-analysis-muted">{f.description}</p>
                  <EndpointTable endpoints={shown} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Subscriptions */}
      <div className="analytics-chart-container">
        <div className="analytics-chart-header">
          <h3>Subscriptions</h3>
          <span className="api-analysis-muted">Prices as of {subscriptions.as_of}</span>
        </div>

        <div className="api-analysis-plans">
          {subscriptions.current.map((p) => (
            <div key={p.key} className="api-analysis-plan">
              <div className="api-analysis-plan-head">
                <strong>{p.name}</strong>
                <span>${p.monthly_usd}/mo</span>
              </div>
              <div className="api-analysis-muted">{p.covers}</div>
              <div className="api-analysis-plan-calls">
                {(summary.by_subscription[p.key] || []).map((id) => (
                  <span key={id} className="api-analysis-pill" title={label(id)}>{id}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <h4>Massive Stocks tiers</h4>
        <div className="api-analysis-table-wrap">
          <table className="api-analysis-table compact">
            <thead>
              <tr><th>Tier</th><th>$/mo</th><th>Stock history</th><th>Timeliness</th><th>Financials</th></tr>
            </thead>
            <tbody>
              {subscriptions.stocks_tiers.map((t) => (
                <tr key={t.name}>
                  <td>{t.name}</td>
                  <td>${t.monthly_usd}</td>
                  <td>{t.history}</td>
                  <td>{t.timeliness}</td>
                  <td>{t.financials ? 'Included' : '$29 add-on'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h4>Cost scenarios</h4>
        <div className="api-analysis-plans">
          {subscriptions.scenarios.map((s) => (
            <div key={s.key} className="api-analysis-plan">
              <div className="api-analysis-plan-head">
                <strong>{s.name}</strong>
                <span>${s.monthly_usd.toFixed(2)}/mo</span>
              </div>
              <ul className="api-analysis-list">
                {s.tradeoffs.map((t) => <li key={t}>{t}</li>)}
              </ul>
            </div>
          ))}
        </div>

        <h4>If Massive Stocks is dropped or downgraded</h4>
        <ul className="api-analysis-list">
          {summary.massive_stocks_dependent.map((id) => {
            const e = endpointsById[id];
            const alt = ALT_LABELS[e.alternative.level];
            return (
              <li key={id}>
                <span className="api-analysis-pill">{id}</span> {e.title}
                {summary.stocks_underlying_dependent.includes(id) && (
                  <Chip tone="bad">underlying for IV</Chip>
                )}
                {' '}<Chip tone={alt.tone}>EODHD: {alt.text}</Chip>
              </li>
            );
          })}
        </ul>
        <p className="api-analysis-muted">{subscriptions.licensing_note}</p>
      </div>

      {/* Housekeeping */}
      <div className="analytics-chart-container">
        <div className="analytics-chart-header"><h3>Notes</h3></div>
        {report.security_notes.map((n) => (
          <div key={n.title} className="api-analysis-security">
            <Chip tone="bad">security</Chip> <strong>{n.title}</strong>
            <div>{n.detail}</div>
            <ul className="api-analysis-list">
              {n.refs.map((r) => <li key={r}><code>{r}</code></li>)}
            </ul>
          </div>
        ))}
        <h4>Next steps</h4>
        <ul className="api-analysis-list">
          {report.next_steps.map((s) => <li key={s}>{s}</li>)}
        </ul>
        <h4>Files with provider URLs but no live calls</h4>
        <ul className="api-analysis-list">
          {Object.entries(report.dead_files).map(([f, why]) => (
            <li key={f}><code>{f}</code> {why}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Kpi({ color, label, value, sub }) {
  return (
    <div className="analytics-kpi-card" style={{ borderLeftColor: color }}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={{ color }}>{value}</div>
      <div className="kpi-sublabel">{sub}</div>
    </div>
  );
}

export default ReportsApiAnalysis;
